import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/context/LanguageContext";
import type { PlayerRef } from "@/hooks/use-player-products";

interface Props {
  players: PlayerRef[] | undefined;
  variant?: "card" | "detail";
  className?: string;
}

/** Pill badge shown when a product is used by one or more pro players. */
const UsedByProBadge = ({ players, variant = "card", className }: Props) => {
  const { lang } = useLanguage();
  if (!players || players.length === 0) return null;
  const first = players[0];
  const extra = players.length - 1;
  const label =
    players.length === 1
      ? first.player_name
      : `${first.player_name} +${extra}`;

  const base =
    variant === "card"
      ? "text-[10px] px-1.5 py-0.5"
      : "text-xs px-2 py-1";

  const href = first.player_slug ? `/${lang}/players/${first.player_slug}` : "#";


  return (
    <Link
      to={href}
      onClick={(e) => {
        if (!first.player_slug) e.preventDefault();
      }}
      className={cn(
        "inline-flex items-center gap-1 rounded-full font-medium border shadow-sm",
        "bg-primary/90 text-primary-foreground border-primary/40 hover:bg-primary",
        "transition-colors",
        base,
        className
      )}
      title={players.map((p) => p.player_name).join(", ")}
    >
      {first.player_photo_url && (
        <img
          src={first.player_photo_url}
          alt=""
          className={cn(
            "rounded-full object-cover bg-background",
            variant === "card" ? "w-3.5 h-3.5" : "w-5 h-5"
          )}
          loading="lazy"
        />
      )}
      <span className="uppercase tracking-wide leading-none">
        {variant === "card" ? "PRO" : "USED BY PRO"}
      </span>
      <span className="normal-case tracking-normal leading-none opacity-95">
        · {label}
      </span>
    </Link>
  );
};

export default UsedByProBadge;
